import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { accessRefused, api, unwrap } from '../api/client'
import { sealAfterRefusal, useSealedQuery } from '../api/sealed'
import { heldEra, inHeldEra, useSessionEra } from '../documents/held'
import { t } from '../i18n'
import { ErrorNotice, Notice, Overlay, Pending, formatDateTime } from '../ui'

/**
 * The instance answers fifty entries at a time (`inbox_checked`, `limit=50`),
 * so a full page is the latest fifty and never all of them.
 */
export const PAGE = 50

/** One project's inbox as read with or without the unread filter, before the era. */
function inboxKey(projectId: string, unreadOnly: boolean): readonly unknown[] {
  return ['engage', 'notifications', projectId, unreadOnly]
}

/** The reads one refusal speaks for: both filters of one project's inbox. */
function inboxScope(projectId: string): string {
  return `engage/notifications/${projectId}`
}

/**
 * Put one project's inbox away, under both filters, for one session.
 *
 * A refusal says the instance declined this reader; it does not say which
 * filter it declined. So a refused read of either list, or a refused mark,
 * seals both, and moves the scope's generation so a read of either that set
 * out before the refusal cannot lift the seal when it lands. Only a read the
 * instance answers afterwards does (`api/sealed.ts`).
 */
export function sealInbox(client: QueryClient, projectId: string, era: number): void {
  for (const unreadOnly of [false, true])
    sealAfterRefusal(client, { key: inboxKey(projectId, unreadOnly), era, scope: inboxScope(projectId) })
}

/** A mark asked for, with the project and session it was asked in. */
type Mark = { id: string; projectId: string; era: number }

/** Feedback from a mark, kept with the project and session it belongs to. */
type Said = { projectId: string; era: number; text: string }

/**
 * Your notifications on this project, and marking one read.
 *
 * The instance has kept everyone's notifications and served them
 * (`GET /engage/notifications`) with nothing on the screen asking. What it
 * answers is shown as it was answered. Each entry is asked about again with
 * the access held now: one the reader may no longer see is left out by the
 * instance, and one whose module cannot be asked comes back as a fixed line
 * that names no record. So the text is the instance's, and no title or link
 * is put back from anything this browser remembers.
 *
 * One at a time: the instance marks one entry read, and only the reader's
 * own. There is no "mark all read", on purpose.
 */
export function NotificationsDrawer({
  projectId,
  onClose,
}: {
  projectId: string
  onClose: () => void
}) {
  const titleId = useId()
  const era = useSessionEra()
  const queries = useQueryClient()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [said, setSaid] = useState<Said | null>(null)

  // Through the shared reader: a refusal seals this inbox until a read of it
  // is answered again, whatever is in the cache and whichever filter is shown.
  const inbox = useSealedQuery({
    key: inboxKey(projectId, unreadOnly),
    era,
    scope: inboxScope(projectId),
    // A refusal is not retried: asking again is not going to change who asked.
    retry: (failures, error) => !accessRefused(error) && failures < 1,
    read: async () => {
      try {
        return unwrap(
          await api.GET('/engage/notifications', {
            params: { query: { project_id: projectId, unread_only: unreadOnly } },
          }),
        )
      } catch (error) {
        if (accessRefused(error)) sealInbox(queries, projectId, era)
        throw error
      }
    },
  })

  // Every answer is settled into the project and session the mark was asked
  // in, from what the mark carried, not from what the drawer shows when it
  // lands. The callbacks are the mutation's, so they run after the drawer has
  // closed or moved on too.
  const markRead = useMutation({
    mutationFn: async ({ id }: Mark) =>
      unwrap(
        await api.POST('/engage/notifications/{notification_id}/read', {
          params: { path: { notification_id: id } },
        }),
      ),
    onSuccess: (answer, mark) => {
      // An answer that lands after the session changed belongs to nobody now.
      if (!inHeldEra(mark.era)) return
      setSaid(
        answer.ok
          ? null
          : { projectId: mark.projectId, era: mark.era, text: t('engage.notifications.not_marked') },
      )
      void queries.invalidateQueries({ queryKey: ['engage', 'notifications', mark.projectId] })
    },
    onError: (error, mark) => {
      // A refused mark is the same evidence as a refused read, and it seals
      // the inbox it was asked from even if the drawer has left it.
      if (accessRefused(error)) sealInbox(queries, mark.projectId, mark.era)
    },
  })

  // What a mark says is shown only where it was asked: on this project, in
  // this session.
  const mark = markRead.variables
  const markHere = mark !== undefined && mark.projectId === projectId && mark.era === era
  const saidHere = said !== null && said.projectId === projectId && said.era === era
  const entries = inbox.data ?? []
  return (
    <Overlay kind="drawer" titleId={titleId} onClose={onClose}>
      <header>
        <h2 id={titleId}>{t('engage.notifications.title')}</h2>
        <button type="button" className="subtle" onClick={onClose}>
          {t('app.close')}
        </button>
      </header>
      <div className="body">
        <label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => setUnreadOnly(event.target.checked)}
          />{' '}
          {t('engage.notifications.unread_only')}
        </label>
        {saidHere ? <Notice tone="warn">{said.text}</Notice> : null}
        {markHere && markRead.error ? <ErrorNotice error={markRead.error} /> : null}
        {inbox.query.isPending ? <Pending /> : null}
        {inbox.query.error ? (
          <ErrorNotice error={inbox.query.error} retry={() => void inbox.query.refetch()} />
        ) : null}
        {inbox.data && entries.length === 0 ? (
          <p className="muted">
            {t(unreadOnly ? 'engage.notifications.none_unread' : 'engage.notifications.none')}
          </p>
        ) : null}
        {entries.length >= PAGE ? (
          <p className="muted">{t('engage.notifications.latest', { count: entries.length })}</p>
        ) : null}
        <ul className="feed">
          {entries.map((entry) => {
            const unread = entry.read_at === null
            return (
              <li key={entry.id} data-unread={unread}>
                {unread ? (
                  <span className="chip" data-tone="open">
                    {t('engage.notifications.unread')}
                  </span>
                ) : null}
                <div>{entry.text}</div>
                <span className="when">{formatDateTime(entry.occurred_at)}</span>
                {unread ? (
                  <button
                    type="button"
                    className="subtle"
                    disabled={markHere && markRead.isPending && mark.id === entry.id}
                    onClick={() => markRead.mutate({ id: entry.id, projectId, era: heldEra() })}
                  >
                    {t('engage.notifications.mark_read')}
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      </div>
    </Overlay>
  )
}
